import { useEffect, useState } from "react";
import { Link, useLocation, useMatch } from "react-router-dom";

import { useCurrentUser } from "../api";
import { useReport } from "../context/ReportContext";
import { COMBAT_LOG_BLOCK, COMBAT_LOG_METRIC, HIDDEN_METRICS } from "../metrics";

export default function Sidebar() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile drawer whenever the user navigates.
  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      {/* Mobile top bar: only visible on small screens */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-bg/95 backdrop-blur px-3 py-2">
        <button
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="p-2 -ml-2 rounded hover:bg-white/5"
        >
          <BurgerIcon />
        </button>
        <span className="font-semibold text-accent text-sm">Log Analyzer</span>
        <span className="w-9" />
      </header>

      {/* Backdrop for mobile drawer */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/60"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={[
          "fixed lg:sticky top-0 z-40 lg:z-auto h-screen w-72",
          "border-r border-border bg-panel",
          "transition-transform duration-200 ease-out",
          "flex flex-col",
          open ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0",
        ].join(" ")}
      >
        <SidebarHeader onClose={() => setOpen(false)} />

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <SidebarBody />
        </nav>

        <footer className="border-t border-border p-3 space-y-1">
          <Link
            to="/admin"
            className={[
              "flex items-center gap-2 rounded px-2 py-1.5 text-xs",
              "text-gray-500 hover:bg-white/5 hover:text-gray-300",
              pathname === "/admin" ? "text-accent" : "",
            ].join(" ")}
          >
            <AdminIcon />
            <span>Admin</span>
          </Link>
          <MasterOnlyLink pathname={pathname} />
          <p className="px-2 pt-1 text-[10px] text-gray-600 leading-tight">
            {"<one guild> log analyzer by jw"}
          </p>
        </footer>
      </aside>
    </>
  );
}

function SidebarHeader({ onClose }: { onClose: () => void }) {
  // Both useMatch hooks must always run — using `||` between them would
  // short-circuit the second call when the first matches, violating the
  // Rules of Hooks (hook count must be stable across renders).
  const matchMetric = useMatch("/r/:slug/:metric");
  const matchSlug = useMatch("/r/:slug");
  const onReport = !!matchMetric || !!matchSlug;
  const { report } = useReport();

  return (
    <div className="border-b border-border px-4 py-4 relative">
      <button
        onClick={onClose}
        aria-label="Close menu"
        className="lg:hidden absolute top-2 right-2 p-1 rounded hover:bg-white/5"
      >
        <CloseIcon />
      </button>
      {onReport && report ? (
        <div className="space-y-2">
          <h1
            className="text-lg font-bold leading-tight tracking-tight text-gray-100"
            title={report.zone}
          >
            {report.zone ?? "Unknown zone"}
          </h1>
          <dl className="space-y-1 text-xs text-gray-400">
            <MetaRow label="Log name" value={report.reportTitle} />
            <MetaRow label="Date of raid" value={formatDate(report.raidStartedAt)} />
            <MetaRow label="Date of import" value={formatDate(report.createdAt)} />
          </dl>
          <a
            href={report.wclUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          >
            View on Warcraft Logs
            <ExternalIcon />
          </a>
        </div>
      ) : (
        <h1 className="text-base font-bold text-accent">Log Analyzer</h1>
      )}
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <dt className="flex-shrink-0 text-gray-500">{label}:</dt>
      <dd className="truncate text-gray-300" title={value}>
        {value}
      </dd>
    </div>
  );
}

function SidebarBody() {
  // The Sidebar lives *outside* the route tree, so useParams() can't see
  // :slug or :metric. Match the path explicitly to get them.
  const match = useMatch("/r/:slug/:metric");
  const slugMatch = useMatch("/r/:slug");
  const slug = match?.params.slug ?? slugMatch?.params.slug ?? null;
  const metricFromUrl = match?.params.metric ?? null;

  const { report, loading, error } = useReport();

  // URL is the source of truth for "should this panel show sections" —
  // even if the context still has a stale report from a previous visit,
  // we don't render section links unless the user is actually on /r/...
  if (!slug) {
    return (
      <p className="px-3 text-xs text-gray-500">
        Open a report link to see its sections here.
      </p>
    );
  }
  if (!report) {
    if (loading) return <p className="px-3 text-xs text-gray-500">Loading report…</p>;
    if (error) return <p className="px-3 text-xs text-red-400">{error}</p>;
    return <p className="px-3 text-xs text-gray-500">Loading report…</p>;
  }

  // Use the explicit order from the backend (Firestore map iteration order is
  // unstable). Fall back to dict key order for old reports without `metricOrder`.
  // Strip out anything in HIDDEN_METRICS so legacy reports don't keep
  // surfacing removed metrics.
  const metricNames = (
    report.metricOrder?.filter((n) => n in (report.metrics ?? {})) ??
    Object.keys(report.metrics ?? {})
  ).filter((n) => !HIDDEN_METRICS.has(n));
  // Combat Log is a virtual section, appended only when enrichment exists.
  if (report.enrichment) metricNames.push(COMBAT_LOG_METRIC);
  const activeMetric = metricFromUrl ?? metricNames[0];

  return (
    <ul className="space-y-0.5">
      {metricNames.map((name) => {
        const block =
          name === COMBAT_LOG_METRIC ? COMBAT_LOG_BLOCK : report.metrics[name];
        const active = name === activeMetric;
        return (
          <li key={name}>
            <Link
              to={`/r/${slug}/${name}`}
              className={[
                "flex items-center justify-between gap-2 rounded px-3 py-2 text-sm font-semibold",
                "transition-colors",
                active
                  ? "bg-accent/10 text-accent"
                  : "text-gray-300 hover:bg-white/5 hover:text-gray-100",
              ].join(" ")}
            >
              <span className="truncate">{block?.title ?? name}</span>
              {block?.error && (
                <span
                  className="text-[10px] uppercase tracking-wide text-red-400"
                  title={String(block.error)}
                >
                  err
                </span>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function formatDate(iso: string | undefined | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function BurgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 6h14M3 10h14M3 14h14" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}
function MasterOnlyLink({ pathname }: { pathname: string }) {
  // Master-only audit link. `useCurrentUser` subscribes to login/logout
  // events so this re-renders the moment the user logs in or out — no
  // page refresh needed. Backend also enforces this via require_master,
  // so a regular admin who hand-types the URL gets a 403.
  const me = useCurrentUser();
  if (!me || me.role !== "master") return null;
  return (
    <Link
      to="/admin/audit"
      className={[
        "flex items-center gap-2 rounded px-2 py-1.5 text-xs",
        "text-gray-500 hover:bg-white/5 hover:text-gray-300",
        pathname === "/admin/audit" ? "text-accent" : "",
      ].join(" ")}
    >
      <AuditIcon />
      <span>Audit log</span>
    </Link>
  );
}

function AuditIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="3" width="12" height="14" rx="1.5" />
      <path d="M7 7h6M7 10h6M7 13h4" />
    </svg>
  );
}

function AdminIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="7" r="3" />
      <path d="M3 17c1.5-3.5 4-5 7-5s5.5 1.5 7 5" />
    </svg>
  );
}
function ExternalIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 3h6v6M9 11l8-8M15 11v6H4V6h6" />
    </svg>
  );
}
